import { useEffect, useMemo, useState } from "react";
import "./TrendsPage.css";
import { getVerticalStacks, getSubStacks, getJobTitles, getTimePeriods, getTerritories } from "./api";
import { MONTH_OPTIONS } from "./trendsFormat";
import MultiSelectDropdown from "./MultiSelectDropdown";
import TrendsSingleRole from "./TrendsSingleRole";
import TrendsTwoRoles from "./TrendsTwoRoles";
import TrendsTwoLocations from "./TrendsTwoLocations";

/**
 * TrendsPage - Job Wages & Inflation Comparison (one rail item in Shell.jsx).
 *
 * React port of the Angular shell component
 * job-wages-and-inflation-comparision.component.ts. Frame styled like the
 * other new pages (JDSkillsPage hero + tabs); logic ported verbatim:
 *
 *   - Vertical Stack multiselect capped at 3 / Sub Stack multiselect
 *     (rendered only when it has options) - these ONLY narrow the job-title
 *     list passed to the tabs client-side, they are never sent to the
 *     salary-comparison API (same as old);
 *   - sub-stack options react to the vertical selection and the sub-stack
 *     selection RESETS whenever vertical changes;
 *   - year range from the working time periods (min year -> max year
 *     defaults), start month 1-12 (Jan default), end month = start - 1
 *     computed, not user-selectable (rolling 12-month review window);
 *   - tabs are lazy-but-persistent: a tab mounts the first time it is
 *     visited and STAYS mounted after (state/filters/chart preserved on
 *     switch) - the old tabVisited[] pattern.
 */
const TABS = [
  { key: "single", label: "Single Role" },
  { key: "two-roles", label: "Two Roles" },
  { key: "two-locations", label: "Role × 2 Locations" },
];

export default function TrendsPage() {
  const [verticalStacks, setVerticalStacks] = useState([]);
  const [allSubStacks, setAllSubStacks] = useState([]);
  const [allJobTitles, setAllJobTitles] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedVerticalIds, setSelectedVerticalIds] = useState([]);
  const [selectedSubStackIds, setSelectedSubStackIds] = useState([]);
  const [startYear, setStartYear] = useState(null);
  const [endYear, setEndYear] = useState(null);
  const [startMonth, setStartMonth] = useState(1);

  const [activeTab, setActiveTab] = useState("single");
  const [tabVisited, setTabVisited] = useState({ single: true });

  useEffect(() => {
    Promise.all([getTimePeriods(), getJobTitles(), getVerticalStacks(), getSubStacks(), getTerritories()])
      .then(([timePeriods, jobTitles, verticals, subStacks, terrs]) => {
        // Year options: unique years from the working time periods
        // (old code regex-extracted the year out of "2024 Q1" names; the
        // Flask /time-periods payload carries year directly - same values).
        const uniqueYears = [...new Set(timePeriods.map((tp) => tp.year).filter(Boolean))].sort();
        setYearOptions(uniqueYears);
        setStartYear(uniqueYears[0] ?? null);
        setEndYear(uniqueYears[uniqueYears.length - 1] ?? null);

        setAllJobTitles(jobTitles);
        setVerticalStacks(verticals.filter((v) => v.is_active));
        setAllSubStacks(subStacks);
        setTerritories(terrs);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Sub-stack options follow the vertical selection (old
  // onVerticalStackSelectionChange). Selection resets on vertical change.
  const availableSubStacks = useMemo(
    () =>
      selectedVerticalIds.length > 0
        ? allSubStacks.filter((s) => selectedVerticalIds.includes(s.vertical_stack_id))
        : [...allSubStacks],
    [allSubStacks, selectedVerticalIds]
  );

  function onVerticalChange(ids) {
    setSelectedVerticalIds(ids || []);
    setSelectedSubStackIds([]);
  }

  // updateFilteredJobTitles() - verbatim cascade, including the safety net.
  const filteredJobTitles = useMemo(() => {
    const byVertical =
      selectedVerticalIds.length > 0
        ? allJobTitles.filter((j) => selectedVerticalIds.includes(j.vertical_stack_id))
        : [...allJobTitles];
    let result =
      selectedSubStackIds.length > 0
        ? byVertical.filter((j) => selectedSubStackIds.includes(j.sub_stack_id))
        : byVertical;
    if (result.length === 0 && selectedSubStackIds.length === 0 && selectedVerticalIds.length === 0) {
      result = [...allJobTitles];
    }
    return result;
  }, [allJobTitles, selectedVerticalIds, selectedSubStackIds]);

  function selectTab(key) {
    setActiveTab(key);
    setTabVisited((v) => ({ ...v, [key]: true }));
  }

  // End month label = the month before start month, wrapping (Jan -> Dec).
  const endMonthLabel = MONTH_OPTIONS[(startMonth === 1 ? 12 : startMonth - 1) - 1].label;

  const tabProps = {
    startYear,
    endYear,
    startMonth,
    jobTitles: filteredJobTitles,
    territories,
  };

  return (
    <div className="tr-page">
      <div className="tr-hero">
        <div className="tr-hero-left">
          <div className="tr-eyebrow">MINERVA INTELLIGENCE &middot; TRENDS</div>
          <h1 className="tr-title">
            Job wages &amp; <em>inflation</em> comparison.
          </h1>
          <p className="tr-desc">
            Salary movement against inflation per review cycle: one role, two
            roles side by side, or one role across two locations. Forecast
            periods are flagged in amber.
          </p>
        </div>
      </div>

      {/* Filter strip styled like the Comp page's FilterBar: chip fields with
          inline small-caps labels, "»" cascade separators, window controls
          grouped on the right - on the same pale page-bg band, no card box. */}
      <div className={`tr-filterbar${loading ? " tr-loading" : ""}`}>
        <MultiSelectDropdown
          title="Vertical Stack "
          options={verticalStacks}
          model={selectedVerticalIds}
          maxSelections={3}
          onChange={onVerticalChange}
        />
        {availableSubStacks.length > 0 && (
          <>
            <span className="tr-fb-sep">&raquo;</span>
            <MultiSelectDropdown
              title="Sub Stack "
              options={availableSubStacks}
              model={selectedSubStackIds}
              onChange={(ids) => setSelectedSubStackIds(ids || [])}
            />
          </>
        )}
        <div className="tr-fb-spacer" />
        <label className="tr-chip">
          <span className="tr-chip-label">From</span>
          <select className="tr-chip-select" value={startYear ?? ""} onChange={(e) => setStartYear(Number(e.target.value))}>
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <span className="tr-fb-arrow">→</span>
        <label className="tr-chip">
          <span className="tr-chip-label">To</span>
          <select className="tr-chip-select" value={endYear ?? ""} onChange={(e) => setEndYear(Number(e.target.value))}>
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        <label className="tr-chip">
          <span className="tr-chip-label">Review Period</span>
          <select className="tr-chip-select" value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
            {MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>
        <span className="tr-fb-arrow">→</span>
        <span className="tr-review-end" title="Rolling 12-month window ending the month before the start month">{endMonthLabel}</span>
      </div>

      <div className="tr-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tr-tab-btn${activeTab === tab.key ? " active" : ""}`}
            onClick={() => selectTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Lazy-but-persistent: mounted on first visit, then kept alive and
          only hidden - so each tab's filters/data survive switching. */}
      <div className="tr-tab-content">
        {tabVisited["single"] && (
          <div style={{ display: activeTab === "single" ? "block" : "none" }}>
            <TrendsSingleRole {...tabProps} />
          </div>
        )}
        {tabVisited["two-roles"] && (
          <div style={{ display: activeTab === "two-roles" ? "block" : "none" }}>
            <TrendsTwoRoles {...tabProps} />
          </div>
        )}
        {tabVisited["two-locations"] && (
          <div style={{ display: activeTab === "two-locations" ? "block" : "none" }}>
            <TrendsTwoLocations {...tabProps} />
          </div>
        )}
      </div>
    </div>
  );
}
